import React, { useState } from 'react'
import { Modal } from 'components/UI/Modal/Modal'
import { toast } from 'react-toastify'
import { useCreateCheckoutSession } from 'services/contactRequest'
import { TextInput } from 'components/FormUtils/TextInput'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

interface PurchaseCreditsModalProps {
  open: boolean
  onClose: () => void
}

const PRICE_PER_CREDIT = 1 // Price in USD per credit

const purchaseCreditsFormSchema = z.object({
  creditAmount: z
    .number()
    .min(1, 'Minimum 1 credit required')
    .max(10000, 'Maximum 10,000 credits allowed'),
})

type PurchaseCreditsForm = z.infer<typeof purchaseCreditsFormSchema>

export function PurchaseCreditsModal({
  open,
  onClose,
}: PurchaseCreditsModalProps) {
  const createCheckoutSession = useCreateCheckoutSession()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, errors },
    reset,
  } = useForm<PurchaseCreditsForm>({
    resolver: zodResolver(purchaseCreditsFormSchema),
    mode: 'onChange',
    defaultValues: {
      creditAmount: 100,
    },
  })

  const creditAmount = watch('creditAmount')
  const totalPrice = creditAmount * PRICE_PER_CREDIT

  const handlePurchase = handleSubmit(async (values) => {
    try {
      setIsSubmitting(true)
      console.log(
        'Creating checkout session for',
        values.creditAmount,
        'credits'
      )

      const response = await createCheckoutSession.mutateAsync({
        amount: values.creditAmount,
        success_url: `${window.location.origin}/contacts/requests?success=true`,
        cancel_url: `${window.location.origin}/contacts/requests?canceled=true`,
      })

      if (response?.url) {
        console.log('Redirecting to Stripe checkout:', response.url)
        // Store callback marker
        localStorage.setItem('creditPurchaseSuccessCallback', 'true')
        // Redirect to Stripe checkout
        window.location.href = response.url
      } else {
        throw new Error('No checkout URL received')
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error)
      toast.error('Failed to start checkout process. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  })

  const previewText = `Purchase ${creditAmount} credits for $${totalPrice.toFixed(2)} (${PRICE_PER_CREDIT} USD per credit)`

  return (
    <Modal
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          reset()
          onClose()
        }
      }}
      title="Purchase Credits"
      description="Credits are used to request contact information"
      onAccept={handlePurchase}
      acceptButtonText={`Purchase Credits ($${totalPrice.toFixed(2)})`}
      blockAccept={!isValid || isSubmitting}
      loading={isSubmitting}
    >
      <div className="space-y-6 mb-4">
        <TextInput
          control={control}
          name="creditAmount"
          label="Number of Credits"
          type="number"
          helperText={errors.creditAmount?.message}
        />

        <div className="px-4 bg-gray-50 rounded-md space-y-3">
          <h3 className="text-lg font-medium">Purchase Summary</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Price per credit:</span>
              <span>${PRICE_PER_CREDIT.toFixed(2)} USD</span>
            </div>
            <div className="flex justify-between">
              <span>Number of credits:</span>
              <span>{creditAmount}</span>
            </div>
            <div className="flex justify-between font-medium text-gray-900 pt-2 border-t">
              <span>Total price:</span>
              <span>${totalPrice.toFixed(2)} USD</span>
            </div>
          </div>
        </div>

        <div className="text-sm text-gray-500">{previewText}</div>
      </div>
    </Modal>
  )
}
